import { ChatScrollAnchor } from './chat-history-message.util';

/** Captures scroll metrics before prepending older messages at the top. */
export interface PrependScrollAnchor
{
  scrollHeight: number;
  scrollTop: number;
}

export function getChatScrollElement(host: HTMLElement | null | undefined): HTMLElement | null
{
  if (!host)
  {
    return null;
  }

  const candidates: HTMLElement[] = [];
  const messageList = host.querySelector('.k-message-list');
  const chatRoot = host.querySelector('.k-chat');
  if (messageList instanceof HTMLElement)
  {
    candidates.push(messageList);
  }

  if (chatRoot instanceof HTMLElement)
  {
    candidates.push(chatRoot);
  }

  for (const element of candidates)
  {
    if (element.scrollHeight > element.clientHeight + 1)
    {
      return element;
    }
  }

  return candidates[0] ?? null;
}

export function isChatAtBottom(host: HTMLElement | null | undefined, thresholdPx = 16): boolean
{
  const scrollElement = getChatScrollElement(host);
  if (!scrollElement)
  {
    return false;
  }

  const distanceFromBottom = scrollElement.scrollHeight - scrollElement.scrollTop - scrollElement.clientHeight;
  return distanceFromBottom <= thresholdPx;
}

export function getMaxChatScrollTop(host: HTMLElement | null | undefined): number
{
  const scrollElement = getChatScrollElement(host);
  if (!scrollElement)
  {
    return 0;
  }

  return Math.max(0, scrollElement.scrollHeight - scrollElement.clientHeight);
}

export function captureChatScrollAnchor(host: HTMLElement | null | undefined): ChatScrollAnchor | null
{
  const scrollElement = getChatScrollElement(host);
  if (!scrollElement)
  {
    return null;
  }

  return {
    nearBottom: isChatAtBottom(host),
    scrollTop: scrollElement.scrollTop
  };
}

export function pinChatScrollToBottom(host: HTMLElement | null | undefined): void
{
  const scrollElement = getChatScrollElement(host);
  if (!scrollElement || !host)
  {
    return;
  }

  const reactionRows = host.querySelectorAll<HTMLElement>('.chat-message-reactions');
  const lastReactionRow = reactionRows?.[reactionRows.length - 1];
  if (lastReactionRow)
  {
    lastReactionRow.scrollIntoView({ block: 'end', inline: 'nearest' });
  }
  else
  {
    const wraps = host.querySelectorAll<HTMLElement>('.chat-history-message-wrap');
    const lastWrap = wraps?.[wraps.length - 1];
    lastWrap?.scrollIntoView({ block: 'end', inline: 'nearest' });
  }

  const maxScrollTop = getMaxChatScrollTop(host);
  if (scrollElement.scrollTop < maxScrollTop - 1)
  {
    scrollElement.scrollTop = maxScrollTop;
  }
}

export function capturePrependScrollAnchor(host: HTMLElement | null | undefined): PrependScrollAnchor | null
{
  const scrollElement = getChatScrollElement(host);
  if (!scrollElement)
  {
    return null;
  }

  return {
    scrollHeight: scrollElement.scrollHeight,
    scrollTop: scrollElement.scrollTop
  };
}

export function restorePrependScrollAnchor(host: HTMLElement | null | undefined, anchor: PrependScrollAnchor | null): void
{
  if (!anchor)
  {
    return;
  }

  const scrollElement = getChatScrollElement(host);
  if (!scrollElement)
  {
    return;
  }

  const heightDelta = scrollElement.scrollHeight - anchor.scrollHeight;
  scrollElement.scrollTop = anchor.scrollTop + heightDelta;
}

export interface ScrollToChatMessageOptions
{
  behavior?: ScrollBehavior;
  block?: ScrollLogicalPosition;
}

function escapeMessageIdSelector(messageId: string): string
{
  return typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
    ? CSS.escape(messageId)
    : messageId.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

/** Scrolls to a message row when it is already in the DOM. */
export function scrollToChatMessage(
  host: HTMLElement | null | undefined,
  messageId: string | number,
  options?: ScrollToChatMessageOptions
): boolean
{
  const chatRoot = host?.querySelector('.k-chat');
  const anchor = chatRoot?.querySelector(`[data-message-id="${escapeMessageIdSelector(String(messageId))}"]`);
  const row = anchor?.closest('.k-message');
  if (!(row instanceof HTMLElement))
  {
    return false;
  }

  row.scrollIntoView({
    behavior: options?.behavior ?? 'smooth',
    block: options?.block ?? 'center'
  });
  return true;
}

/**
 * Polls with requestAnimationFrame until the message row exists, then scrolls.
 * Stops when {@link shouldContinue} returns false or {@link maxFrames} is reached.
 */
export function scrollToChatMessageWhenPresent(
  host: HTMLElement | null | undefined,
  messageId: string | number,
  options: ScrollToChatMessageOptions & {
    maxFrames?: number;
    shouldContinue?: () => boolean;
    onComplete?: (found: boolean) => void;
  } = {}
): void
{
  const maxFrames = options.maxFrames ?? 90;
  let frames = 0;

  const tick = (): void =>
  {
    if (options.shouldContinue && !options.shouldContinue())
    {
      options.onComplete?.(false);
      return;
    }

    if (scrollToChatMessage(host, messageId, options))
    {
      options.onComplete?.(true);
      return;
    }

    frames++;
    if (frames < maxFrames)
    {
      requestAnimationFrame(tick);
    }
    else
    {
      options.onComplete?.(false);
    }
  };

  requestAnimationFrame(tick);
}

export function applyChatScrollAnchor(host: HTMLElement | null | undefined, anchor: ChatScrollAnchor | null): void
{
  if (!anchor)
  {
    return;
  }

  const scrollElement = getChatScrollElement(host);
  if (!scrollElement)
  {
    return;
  }

  if (anchor.nearBottom)
  {
    pinChatScrollToBottom(host);
  }
  else
  {
    scrollElement.scrollTop = anchor.scrollTop;
  }
}
